/*
 * Some Day's Events Report - Controller
 * Angular module: trackerbase-app.views.trackerbase-reports.some-days-events-report
 * States: tbReports
 *      .tomorrowsEventsReport (/trackerbase-reports/tomorrows-events/)
 *      .todaysEventsReport (/trackerbase-reports/todays-events/)
 *      .yesterdaysEventsReport (/trackerbase-reports/yesterdays-events/)
 *      .scheduledEventsReport (/trackerbase-reports/scheduled-events/{year}/{month}/{day}/)
 *
 * Exposed Controller: SomeDaysEventsReportCtrl
 * Controller in template as: someDaysEventsReport
 *
 */

import moment from "moment-timezone";

import Assert from "tbAssert";
import OfficeType from "tbClient/models/OfficeType";
import EventFilter from "tbClient/models/EventFilter";

import MessageQueue from "tbClient/models/MessageQueue";
import SortAsComparer from "tbClient/utility/SortAsComparer";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrapModule from "angular-ui-bootstrap";
import uiRouterModule from "@uirouter/angularjs";

import TimeZoneNameFilterModule from "tbClient/filters/FormatTimeZoneNameFilter";
import CurrentTimeZoneServiceModule from "tbClient/services/CurrentTimeZoneService";
import TrackerBaseReportsDataServiceModule from "tbClient/services/TrackerbaseReportsDataService";
import AllOfficeTypesServiceModule from "tbClient/services/AllOfficeTypesService";
import AllTrackingStatusesServiceModule from "tbClient/services/AllTrackingStatusesService";
import UserMessagesServiceModule from "tbClient/services/UserMessagesService";

const mDocumentSvc = new WeakMap();
const mQSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mTimeoutSvc = new WeakMap();
const mTbReportsDataSvc = new WeakMap();
const mUserMessagesSvc = new WeakMap();

const infoMessageType = "info";
const successMessageType = "success";
const errorMessageType = "danger";
const defaultMessageType = infoMessageType;
const defaultMessageTimeout = 2000;
const mUiMessages = new WeakMap();

const timeFormat = "h:mm A";

const mReportTargetExact = new WeakMap();
const mReportTimeZone = new WeakMap();
const mReportTargetStart = new WeakMap();
const mReportTargetEnd = new WeakMap();

const mReportTitle = new WeakMap();

const mReportEvents = new WeakMap();

const mAllOfficeTypes = new WeakMap();
const mAllTrackingStatusesSvc = new WeakMap();
const _scheduledStatusName = "Scheduled";

const mCanSendReport = new WeakMap();

const mFeatureToggles = new WeakMap();


function makePageTitles(targetMoment, reportTimeZone) {
    let checkDate = moment().tz(reportTimeZone);

    let reportPageTitle = null;
    let reportDisplayTitle = null;

    if (targetMoment.isSame(checkDate, "day")) {
        reportPageTitle = "TrackerBase: Today's Tracking Events";
        reportDisplayTitle = "Today's Events";
    }

    checkDate.add(1, "d");
    if ((reportPageTitle == null) &&
        (targetMoment.isSame(checkDate, "day")))
    {
        reportPageTitle = "Tomorrow's Tracking Events";
        reportDisplayTitle = "Tomorrow's Events";
    }

    checkDate.subtract(2, "d"); //From reportTarget to yesterday.
    if ((reportPageTitle == null) &&
        (targetMoment.isSame(checkDate, "day")))
    {
        reportPageTitle = "TrackerBase: Yesterday's Tracking Events";
        reportDisplayTitle = "Yesterday's Events";
    }

    else if (reportPageTitle == null) {
        reportPageTitle = "TrackerBase: Scheduled Tracking Events for " + targetMoment.format("dddd, MMMM Do, YYYY");
        reportDisplayTitle = "Scheduled Events";
    }

    return {
        reportDisplayTitle: reportDisplayTitle,
        reportPageTitle: reportPageTitle
    };
}

class SomeDaysEventsReportController {
    constructor(
        $document,
        $q,
        $log,
        $timeout,
        $state,
        TrackerBaseReportsData,
        CurrentTimeZone,
        AllOfficeTypes,
        AllTrackingStatuses,
        UserMessages,
        reportTargetDate
    ) {

        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($timeout != null, "Assertion Failure: Missing Timeout Service Dependency");
        Assert.StrictAssert($state != null, "Assertion Failure: Missing UI Router State Service Dependency");

        Assert.StrictAssert(TrackerBaseReportsData != null, "Assertion Failure: Missing TrackerBase Reports Data Service Dependency");
        Assert.StrictAssert(CurrentTimeZone != null, "Assertion Failure: Missing Current Time Zone Service Dependency");
        Assert.StrictAssert(AllOfficeTypes != null, "Assertion Failure: Missing All Office Types Service Dependency");
        Assert.StrictAssert(AllTrackingStatuses != null, "Assertion Failure: Missing All Tracking Statuses Service Dependency");
        Assert.StrictAssert(UserMessages != null, "Assertion Failure: Missing User Messages Service Dependency");
        Assert.StrictAssert(moment.isMoment(reportTargetDate), "Assertion Failure: Report Target Date Is Not A Moment");

        mDocumentSvc.set(this, $document);
        mQSvc.set(this, $q);
        mLogSvc.set(this, $log);
        mTimeoutSvc.set(this, $timeout);

        let featureTogglesData = $state.current.data.featureToggles;
        if (featureTogglesData == null) {
            featureTogglesData = {};
        }
        mFeatureToggles.set(this, Object.assign({}, featureTogglesData));

        mTbReportsDataSvc.set(this, TrackerBaseReportsData);
        mUserMessagesSvc.set(this, UserMessages);

        mUiMessages.set(this, new MessageQueue(defaultMessageType, defaultMessageTimeout));

        mReportTimeZone.set(this, CurrentTimeZone.get());

        mReportTargetExact.set(this, reportTargetDate);

        let reportPageTitle = this._makePageTitles(mReportTargetExact.get(this));
        mReportTitle.set(this, reportPageTitle);

        mReportTargetStart.set(this, mReportTargetExact.get(this).clone().startOf("day"));
        mReportTargetEnd.set(this, mReportTargetExact.get(this).clone().endOf("day"));

        mReportEvents.set(this, {});

        mAllOfficeTypes.set(
            this,
            AllOfficeTypes.AvailableOfficeTypes
                .map((oType) => OfficeType.Copy(oType))
                .sort(SortAsComparer)
        );

        mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);
        this._loadReport();
    }

    _loadReport() {
        const uiMsgSvc = mUiMessages.get(this);
        const usrMsgSvc = mUserMessagesSvc.get(this);
        const logSvc = mLogSvc.get(this);

        mCanSendReport.set(this, false);

        let loadingMessageId = uiMsgSvc.publishMessage(
            usrMsgSvc.getUserMessage({msgId: 0x2004}),
            MessageQueue.timeoutOverrideValue,
            infoMessageType);

        this._getTargetDatesEvents()
            .then((results) => {
                logSvc.debug("Get Target Date Event Results:");
                logSvc.debug(results);

                uiMsgSvc.removeMessageById(loadingMessageId);
                uiMsgSvc.publishMessage(
                    usrMsgSvc.getUserMessage({msgId: 0x2005}),
                    defaultMessageTimeout,
                    successMessageType);
                mCanSendReport.set(this, true);
            })
            .catch((errorResponses) => {
                uiMsgSvc.removeMessageById(loadingMessageId);
                if (errorResponses.length > 0) {
                    uiMsgSvc.publishMessage(
                        usrMsgSvc.getUserMessage({msgId: 0x2A02}),
                        MessageQueue.timeoutOverrideValue,
                        errorMessageType
                    );
                }
            });
    }

    get messages() {
        return mUiMessages.get(this).messages;
    }

    get reportTimeZone() {
        return mReportTimeZone.get(this);
    }

    get reportTitle() {
        return mReportTitle.get(this);
    }

    get targetDateForReport() {
        return mReportTargetExact.get(this).toDate();
    }

    get reportEvents() {
        return mReportEvents.get(this);
    }

    get allOfficeTypes() {
        return mAllOfficeTypes.get(this);
    }

    get canSendReport() {
        return mCanSendReport.get(this);
    }

    get sendReportEnabled() {
        return ((mFeatureToggles.get(this).sendReportFeature) !== false);
    }

    removeMessage(msgIdx) {
        mUiMessages.get(this).removeMessageAtIdx(msgIdx);
    }

    showInCurrentTimeZone(evtMoment) {
        if (moment.isMoment(evtMoment)) {
            return evtMoment.clone().tz(mReportTimeZone.get(this)).format(timeFormat);
        }
        else {
            return undefined;
        }
    }

    sendReport() {
        const sendReportPromise = this._sendReportImpl();
        sendReportPromise.then(() => {
            mLogSvc.get(this).debug("Send Report async processing finished.");
        });
    }

    _sendReportImpl() {
        const logSvc = mLogSvc.get(this);

        if (!this.sendReportEnabled) {
            logSvc.info("Report sending is currently disabled.");
            return Promise.resolve(null);
        }

        const userMessagesSvc = mUserMessagesSvc.get(this);
        const uiMessages = mUiMessages.get(this);
        const reportSendInProgressMsgId = uiMessages.publishMessage(
            userMessagesSvc.getUserMessage({msgId: 0x2002}),
            MessageQueue.timeoutOverrideValue,
            defaultMessageType
        );

        return mTbReportsDataSvc.get(this).sendSomeDaysScheduledEventsReport({
            targetDate: mReportTargetExact.get(this),
            timeZone: mReportTimeZone.get(this)
        })
        .then((success) => mTimeoutSvc.get(this)(() => {
            if (success === true) {
                uiMessages.removeMessageById(reportSendInProgressMsgId);
                uiMessages.publishMessage(
                    userMessagesSvc.getUserMessage({msgId: 0x2003}),
                    defaultMessageTimeout,
                    successMessageType
                );
            }
            else {
                throw new Error("Report sent successfully, but there was an error notifying the user.");
            }
        }, 0))
        .catch((err) => mTimeoutSvc.get(this)(() => {
            const baseErrorMessage = userMessagesSvc.getUserMessage({msgId: 0x2A01});

            uiMessages.removeMessageById(reportSendInProgressMsgId);
            uiMessages.publishMessage(`${baseErrorMessage}: ${err.message}`,
                MessageQueue.timeoutOverrideValue,
                errorMessageType
            );

            throw err;
        }))
        .catch((err) => {
            //TODO: Handle errors that can be handled.
            logSvc.error("An error occurred when requesting that the tracking schedule report be sent.", err);
            // Swallow error here after logging it.
        });
    }

    _makePageTitles(targetMoment) {
        let pageTitleData = makePageTitles(targetMoment, mReportTimeZone.get(this));
        mDocumentSvc.get(this).attr("title", pageTitleData.reportPageTitle);

        return pageTitleData.reportDisplayTitle;
    }

    // EventFilter form of 'const officeTypeEventFilterBase = "and||status|scheduled||officetype|'
    _makeOfficeTypeEventFilterForOfficeType(officeTypeId) {
        const scheduledStatus = mAllTrackingStatusesSvc.get(this).getFromName(_scheduledStatusName);
        return new EventFilter({
            includeStatusFilter: [scheduledStatus.trackingStatusId],
            officeTypeFilter: [officeTypeId]
        });
    }

    _getTargetDatesEventsForSingleOfficeType(oType) {

        let eventQueryFilter =  this._makeOfficeTypeEventFilterForOfficeType(oType.officeTypeId);

        let reportEventsPromise =
            mTbReportsDataSvc.get(this)
                .getEventsInDateRange({
                    start: mReportTargetStart.get(this),
                    end: mReportTargetEnd.get(this),
                    eventFilter: eventQueryFilter
                })
                .then((eventsData) => {
                    Array.prototype.push.apply(
                        this.reportEvents[oType.officeTypeName],
                        eventsData.eventsInRange
                    );
                })
                .catch((errorResponse) => {
                    mLogSvc.get(this).error(errorResponse);
                    throw errorResponse;
                });

        return reportEventsPromise;
    }

    _getTargetDatesEvents() {

        let allGetEventsPromises = [];

        //Note: was AllOfficeTypes.AvailableOfficeTypes in original
        mAllOfficeTypes.get(this).forEach(
            (oType) => {
                this.reportEvents[oType.officeTypeName] = [];
                let getEventsPromise = this._getTargetDatesEventsForSingleOfficeType(oType);
                allGetEventsPromises.push(getEventsPromise);
            }
        );

        return mQSvc.get(this).all(allGetEventsPromises);
    }

    getReportEventCount() {
        const allOfficeTypes = mAllOfficeTypes.get(this);
        const currentReportEvents = this.reportEvents;
        const reportEventCounts = allOfficeTypes
            .filter((oType) => currentReportEvents[oType.officeTypeName] != null)
            .map((oType) => currentReportEvents[oType.officeTypeName].length)
            .reduce((currentTotal, oTypeEventsCount) => currentTotal + oTypeEventsCount, 0);
        return reportEventCounts;
    }
}

SomeDaysEventsReportController.$inject = [
    "$document",
    "$q",
    "$log",
    "$timeout",
    "$state",
    "TrackerBaseReportsData",
    "CurrentTimeZone",
    "AllOfficeTypes",
    "AllTrackingStatuses",
    "UserMessages",
    "reportTargetDate"
];

SomeDaysEventsReportController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiBootstrapModule,
    uiRouterModule,
    TimeZoneNameFilterModule,
    CurrentTimeZoneServiceModule,
    TrackerBaseReportsDataServiceModule,
    AllOfficeTypesServiceModule,
    UserMessagesServiceModule,
    AllTrackingStatusesServiceModule
];

SomeDaysEventsReportController.template = require("./SomeDaysEventsReport.tpl.html");

SomeDaysEventsReportController.controllerAs = "someDaysEventsReport";

export default SomeDaysEventsReportController;




//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-reports/SomeDaysEventsReportController.js
// module id = CNHa
// module chunks = 1